import { useState } from "react"
import { Plus, Pencil, Trash2, Tag, GripVertical } from "lucide-react"
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core"
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import {
  useTaskCategories,
  useCreateTaskCategory,
  useUpdateTaskCategory,
  useDeleteTaskCategory,
} from "@/hooks/use-task-categories"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import { toast } from "sonner"
import type { TaskCategory } from "@/lib/types"

// ── Color palette ──────────────────────────────────────────────

const CATEGORY_COLORS = [
  "#3B82F6", "#8B5CF6", "#EC4899", "#F59E0B", "#F97316",
  "#22C55E", "#14B8A6", "#06B6D4", "#6366F1", "#EF4444",
  "#84CC16", "#A855F7", "#0EA5E9", "#D946EF", "#78716C",
]

// ── Category Form Dialog ───────────────────────────────────────

interface CategoryFormData {
  name: string
  color: string
}

function CategoryFormDialog({
  open,
  onOpenChange,
  category,
  onSave,
  isPending,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  category: CategoryFormData | null
  onSave: (data: CategoryFormData) => void
  isPending: boolean
}) {
  const [name, setName] = useState(category?.name ?? "")
  const [color, setColor] = useState(category?.color ?? "#3B82F6")
  const [customColor, setCustomColor] = useState("")

  const inputClass = "h-9 w-full rounded-md border bg-transparent px-3 text-sm outline-none"

  const handleSave = () => {
    if (!name.trim()) return
    onSave({ name: name.trim(), color })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{category ? "Edytuj kategorie" : "Nowa kategoria"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-muted-foreground">
              Nazwa *
            </label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={`${inputClass} border-input text-foreground`}
              placeholder="np. Pilne, Marketing, Sprzedaz"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSave()
              }}
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-muted-foreground">
              Kolor
            </label>
            <div className="flex flex-wrap gap-2">
              {CATEGORY_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  className={`h-7 w-7 rounded-full border-2 transition-transform hover:scale-110 ${
                    color === c ? "border-foreground" : "border-transparent"
                  }`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
            <div className="flex items-center gap-2 mt-2">
              <input
                type="text"
                value={customColor}
                onChange={(e) => {
                  setCustomColor(e.target.value)
                  if (/^#[0-9a-fA-F]{6}$/.test(e.target.value)) {
                    setColor(e.target.value)
                  }
                }}
                className={`${inputClass} border-input text-foreground max-w-[140px]`}
                placeholder="#RRGGBB"
              />
              <div
                className="h-7 w-7 shrink-0 rounded-full border"
                style={{ backgroundColor: color }}
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Anuluj
          </Button>
          <Button onClick={handleSave} disabled={!name.trim() || isPending}>
            {isPending ? "Zapisywanie..." : "Zapisz"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ── Sortable Category Row ──────────────────────────────────────

function SortableCategoryRow({
  category,
  onEdit,
  onDelete,
}: {
  category: TaskCategory
  onEdit: () => void
  onDelete: () => void
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 rounded-lg border px-3 py-2.5 transition-colors"
      {...attributes}
    >
      <button
        {...listeners}
        className="cursor-grab touch-none rounded p-0.5 hover:bg-muted text-muted-foreground"
      >
        <GripVertical size={16} />
      </button>
      <div
        className="h-4 w-4 shrink-0 rounded-full"
        style={{ backgroundColor: category.color }}
      />
      <div className="flex-1 min-w-0">
        <span className="text-sm font-medium text-foreground">
          {category.name}
        </span>
      </div>
      <span className="text-xs font-mono text-muted-foreground">
        {category.color}
      </span>
      <div className="flex items-center gap-1">
        <button
          onClick={onEdit}
          className="rounded p-1.5 transition-colors hover:bg-muted text-muted-foreground"
        >
          <Pencil size={14} />
        </button>
        <button
          onClick={onDelete}
          className="rounded p-1.5 transition-colors hover:bg-black/5 text-destructive"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  )
}

// ── Main Page ──────────────────────────────────────────────────

export function TaskCategoriesPage() {
  const { data, isLoading } = useTaskCategories()
  const createMutation = useCreateTaskCategory()
  const updateMutation = useUpdateTaskCategory()
  const deleteMutation = useDeleteTaskCategory()

  const [showAdd, setShowAdd] = useState(false)
  const [editing, setEditing] = useState<TaskCategory | null>(null)
  const [categories, setCategories] = useState<TaskCategory[]>([])
  const [initialized, setInitialized] = useState(false)

  const serverCategories = data?.data ?? []

  // Sync server data to local state
  if (!initialized && serverCategories.length > 0) {
    setCategories(serverCategories)
    setInitialized(true)
  }
  if (initialized && serverCategories.length > 0 && !createMutation.isPending && !updateMutation.isPending && !deleteMutation.isPending) {
    // Keep in sync after mutations
    if (JSON.stringify(serverCategories.map(c => c.id)) !== JSON.stringify(categories.map(c => c.id))) {
      setCategories(serverCategories)
    }
  }

  const displayCategories = initialized ? categories : serverCategories

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = displayCategories.findIndex((c) => c.id === active.id)
    const newIndex = displayCategories.findIndex((c) => c.id === over.id)
    const reordered = arrayMove(displayCategories, oldIndex, newIndex)
    setCategories(reordered)

    // Persist sort order for each reordered item
    try {
      for (let i = 0; i < reordered.length; i++) {
        if (reordered[i].sort_order !== i) {
          await updateMutation.mutateAsync({ id: reordered[i].id, data: { sort_order: i } })
        }
      }
    } catch {
      toast.error("Nie udalo sie zapisac kolejnosci")
    }
  }

  const handleCreate = async (formData: CategoryFormData) => {
    try {
      await createMutation.mutateAsync({
        name: formData.name,
        color: formData.color,
        sort_order: displayCategories.length,
      })
      toast.success("Kategoria utworzona")
      setShowAdd(false)
    } catch {
      toast.error("Nie udalo sie utworzyc kategorii")
    }
  }

  const handleUpdate = async (formData: CategoryFormData) => {
    if (!editing) return
    try {
      await updateMutation.mutateAsync({
        id: editing.id,
        data: { name: formData.name, color: formData.color },
      })
      toast.success("Kategoria zaktualizowana")
      setEditing(null)
    } catch {
      toast.error("Nie udalo sie zaktualizowac kategorii")
    }
  }

  const handleDelete = async (category: TaskCategory) => {
    if (!confirm(`Czy na pewno chcesz usunac kategorie "${category.name}"?`)) return
    try {
      await deleteMutation.mutateAsync(category.id)
      setCategories((prev) => prev.filter((c) => c.id !== category.id))
      toast.success("Kategoria usunieta")
    } catch {
      toast.error("Nie udalo sie usunac kategorii")
    }
  }

  if (isLoading) {
    return (
      <div className="mx-auto max-w-[800px] space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-[800px] space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Tag size={22} className="text-primary" />
          <div>
            <h1 className="page-title">Kategorie zadan</h1>
            <p className="text-sm text-muted-foreground">
              Organizuj zadania za pomoca kategorii z kolorami
            </p>
          </div>
        </div>
        <Button onClick={() => setShowAdd(true)}>
          <Plus size={16} /> Nowa kategoria
        </Button>
      </div>

      {displayCategories.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-input py-12 text-muted-foreground">
          <Tag size={40} />
          <p className="text-sm">Nie masz jeszcze zadnych kategorii zadan</p>
          <Button variant="outline" onClick={() => setShowAdd(true)}>
            <Plus size={16} /> Utworz pierwsza kategorie
          </Button>
        </div>
      ) : (
        <div className="rounded-lg border border-border">
          <div className="space-y-2 p-4">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={displayCategories.map((c) => c.id)}
                strategy={verticalListSortingStrategy}
              >
                {displayCategories.map((category) => (
                  <SortableCategoryRow
                    key={category.id}
                    category={category}
                    onEdit={() => setEditing(category)}
                    onDelete={() => handleDelete(category)}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </div>
        </div>
      )}

      {/* Add Dialog */}
      <CategoryFormDialog
        open={showAdd}
        onOpenChange={setShowAdd}
        category={null}
        onSave={handleCreate}
        isPending={createMutation.isPending}
      />

      {/* Edit Dialog */}
      {editing && (
        <CategoryFormDialog
          open={true}
          onOpenChange={() => setEditing(null)}
          category={{ name: editing.name, color: editing.color }}
          onSave={handleUpdate}
          isPending={updateMutation.isPending}
        />
      )}
    </div>
  )
}
